import { cn } from '../cn.js';
import { ChartTooltip } from './internal/ChartTooltip.js';
import { useChartHover } from './internal/useChartHover.js';
import { useDrawIn } from './internal/useDrawIn.js';

export interface LineChartPoint {
  /** Tick label under the point, e.g. "Q1 2024". */
  x: string;
  /** `null` marks this index as having no value for this series — e.g. a cycle whose peer
   *  benchmark was suppressed by the k-anonymity floor. The point's x-position is still reserved
   *  (every series must have the same `points.length`, index-aligned to the same labels), so the
   *  line simply breaks and resumes around the gap instead of the whole series being dropped or
   *  every series being forced to the same shorter length. */
  y: number | null;
}

export interface LineChartSeries {
  id: string;
  points: LineChartPoint[];
  /** A token reference, e.g. "var(--accent)" — never a literal colour. */
  color?: string;
  /** Rendered as a dashed stroke — the peer-benchmark line, not the caller's own trajectory. */
  dashed?: boolean;
  /** Renders each point's value as a small pill above it — for a short series (4-8 points) where
   *  reading exact values off the axis isn't practical. Never on a dashed/benchmark series: a
   *  peer-benchmark line showing its own value badges at every point competes visually with the
   *  caller's own trajectory, which is the one the reader should be scanning. */
  showValueLabels?: boolean;
  /** Fills the area under the line down to the axis baseline, in a light tint of the series colour.
   *  Only meaningful on a non-dashed series — a benchmark/reference line stays a plain stroke. Each
   *  contiguous run of non-null points gets its own filled shape, same as the stroke itself. */
  area?: boolean;
}

export interface LineChartProps {
  series: LineChartSeries[];
  /** A fixed horizontal marker (a target score) drawn across the full width. */
  target?: { value: number; label?: string; color?: string };
  /** Draws a low-opacity `accent-subtle` band behind the plot from this label index through to the
   *  end — "this is where things stand now" for a trend chart, e.g. the most recent 1-2 cycles. An
   *  index into the shared label list, not a pixel coordinate. `null`/omitted/out-of-range draws
   *  nothing. Same convention as `BarChart`'s identically-named prop. */
  highlightFromIndex?: number;
  /** Formats a value for the hover tooltip. Defaults to the raw number. */
  valueFormat?: (y: number) => string;
  /** A small legend row (swatch + id). Defaults to `series.length >= 2` — a single series needs no
   *  legend, it's already the whole chart. */
  legend?: boolean;
  /** Where the legend row renders relative to the `<svg>`. Defaults to `'bottom'`, matching every
   *  existing caller exactly. */
  legendPosition?: 'top' | 'bottom';
  /** Renders horizontal Y-axis gridlines with a numeric tick label (via `valueFormat`) at the left
   *  edge, evenly spaced across the chart's own already-computed `minY`/`maxY` range below — NOT a
   *  forced 0-based axis, same reasoning as that computation. Off by default so a compact overlay
   *  chart (the normal-curve reference under Rating Distribution) stays visually unchanged unless a
   *  caller opts in. */
  showYAxis?: boolean;
  height?: number;
  className?: string;
  ariaLabel?: string;
}

const BASE_PADDING = { top: 16, right: 12, bottom: 24, left: 12 };
// Value-label pills sit 26px above their point, ~8px tall — extra top padding only when at least
// one series actually draws them, so charts without labels keep the tighter default.
const LABEL_PADDING_TOP = 40;
// Extra left padding reserved for the Y-axis tick label column (a short numeric string, e.g.
// "4.5") when `showYAxis` is on — same conditional-padding convention as `LABEL_PADDING_TOP`
// above, so a chart that doesn't use the feature keeps the tighter default margin.
const AXIS_PADDING_LEFT = 32;
const VIEW_WIDTH = 600;
// Beyond this many x-axis tick labels, render only every other one (endpoints always kept) — at
// narrow container widths (~360px) `preserveAspectRatio="none"` squashes text horizontally, and a
// dense label run collides well before that squash makes it illegible on its own.
const LABEL_SKIP_THRESHOLD = 5;
// Same draw-in timing convention as `BarChart`'s bars — long enough to read as a deliberate
// reveal, short enough not to feel like a loading stall.
const DRAW_IN_DURATION = '560ms';
// Gridlines including both endpoints (minY and maxY) — e.g. a 3.0-5.0 range renders 3.0/3.5/4.0/
// 4.5/5.0, mirroring the Stitch reference's Y axis.
const Y_AXIS_TICK_COUNT = 5;

interface PlottedPoint {
  x: number;
  y: number;
}

/** Contiguous runs of non-null points, in plotted (SVG) coordinates — a null `y` breaks the run.
 *  Rendering one `<polyline>`/fill shape per run is what lets a series skip a suppressed cycle
 *  without either dropping that cycle's x-position (which would misalign it against the other
 *  series) or hiding the entire line because of one gap. */
function buildSegments(
  points: LineChartPoint[],
  xFor: (index: number) => number,
  yFor: (value: number) => number,
) {
  const segments: PlottedPoint[][] = [];
  let current: PlottedPoint[] = [];
  points.forEach((p, i) => {
    if (p.y == null) {
      if (current.length > 0) segments.push(current);
      current = [];
      return;
    }
    current.push({ x: xFor(i), y: yFor(p.y) });
  });
  if (current.length > 0) segments.push(current);
  return segments;
}

/**
 * Multi-series trend line — a score trajectory against a peer-benchmark line, with an optional
 * target marker and an optional filled area under the primary series. Hand-rolled SVG, same
 * reasoning as `Ring.tsx`: no `<defs>`/gradients, so two charts on one page never fight over a
 * shared id, and no charting dependency exists anywhere in this repo to pull in for a
 * handful-of-points line.
 *
 * Every series must share the same `points.length`, index-aligned to the same labels (use
 * `y: null` for a cycle a series has no value for, rather than omitting that entry) — this is what
 * lets a benchmark series skip a k-anonymity-suppressed cycle without shifting every later point
 * out of alignment with the primary series.
 *
 * `viewBox`-scaled to a fixed internal coordinate space and rendered at 100% width, so it reflows
 * with its container instead of needing a measured pixel width from the caller.
 */
export function LineChart({
  series,
  target,
  highlightFromIndex,
  valueFormat = (y) => String(y),
  legend = series.length >= 2,
  legendPosition = 'bottom',
  showYAxis = false,
  height = 220,
  className,
  ariaLabel = 'Score trend chart',
}: LineChartProps) {
  const [drawInRef, mounted] = useDrawIn<HTMLDivElement>();
  const hasValueLabels = series.some((s) => s.showValueLabels && !s.dashed);
  const PADDING = {
    ...BASE_PADDING,
    top: hasValueLabels ? LABEL_PADDING_TOP : BASE_PADDING.top,
    left: showYAxis ? BASE_PADDING.left + AXIS_PADDING_LEFT : BASE_PADDING.left,
  };
  const allValues = series.flatMap((s) =>
    s.points.map((p) => p.y).filter((y): y is number => y != null),
  );
  if (target) allValues.push(target.value);
  // Zoomed to the data's own range plus a 10% margin, NOT forced down to 0 — a rating-scale
  // series (say 3.6-4.6) forced onto a 0-based axis spends 80% of the chart's height on empty
  // space below every point it ever plots, which is exactly backwards for "make the trend
  // legible". A trend line only needs to show how values move relative to each other.
  const rawMin = allValues.length > 0 ? Math.min(...allValues) : 0;
  const rawMax = allValues.length > 0 ? Math.max(...allValues) : 1;
  const margin = (rawMax - rawMin) * 0.1 || 1;
  const minY = rawMin - margin;
  const maxY = rawMax + margin;
  const yRange = maxY - minY || 1;
  // Evenly spaced across the SAME zoomed minY-maxY range above — deliberately not a separate
  // "nice round number" domain calculation, so the gridlines always line up exactly with where
  // the series lines actually plot.
  const yAxisTicks = showYAxis
    ? Array.from(
        { length: Y_AXIS_TICK_COUNT },
        (_, i) => minY + (yRange * i) / (Y_AXIS_TICK_COUNT - 1),
      )
    : [];
  // The caller's `valueFormat` is tuned for its own data values (e.g. a rating's natural 1
  // decimal place) — reusing it for axis ticks breaks down whenever the zoomed range is tight
  // enough that consecutive ticks are less than 0.1 apart (e.g. a 3.9-4.2 score range): two
  // adjacent ticks then round to the identical displayed string ("4.0" appearing twice), which
  // reads as a rendering bug even though the underlying values are correct. The axis label needs
  // just enough decimal places that its own tick STEP is representable, independent of whatever
  // precision the caller chose for point values/tooltips.
  const tickStep = yRange / (Y_AXIS_TICK_COUNT - 1);
  const axisDecimals = Math.max(1, -Math.floor(Math.log10(tickStep)));
  const formatAxisTick = (value: number) => value.toFixed(axisDecimals);

  // Same collision the axis ticks above guard against, but from real data rather than an evenly
  // spaced step: two genuinely different point values (e.g. 3.98 and 4.04) both plot at their own
  // correct, different heights, but the caller's `valueFormat` is tuned for its typical case (one
  // decimal place), so both can round to the identical displayed label ("4.0" twice) — which reads
  // as the chart mis-plotting a point, when the plot was right and only the label collided. Derived
  // from the SMALLEST gap between any two distinct values actually carrying a label (only
  // `showValueLabels` series draw one), not a fixed tick step — enough decimal places that no two
  // real values that are actually different are ever displayed the same.
  const labeledValues = Array.from(
    new Set(
      series
        .filter((s) => s.showValueLabels && !s.dashed)
        .flatMap((s) => s.points.map((p) => p.y).filter((y): y is number => y != null)),
    ),
  ).sort((a, b) => a - b);
  const minLabelGap =
    labeledValues.length > 1
      ? Math.min(...labeledValues.slice(1).map((v, i) => v - labeledValues[i]!))
      : undefined;
  const labelDecimals =
    minLabelGap && minLabelGap > 0 ? Math.max(1, -Math.floor(Math.log10(minLabelGap))) : 1;
  const formatPointLabel = (value: number) => value.toFixed(labelDecimals);

  const plotWidth = VIEW_WIDTH - PADDING.left - PADDING.right;
  const plotHeight = height - PADDING.top - PADDING.bottom;
  const plotBottom = height - PADDING.bottom;
  const plotRight = VIEW_WIDTH - PADDING.right;

  const labels = series[0]?.points.map((p) => p.x) ?? [];
  const count = labels.length;
  const xFor = (index: number) =>
    count <= 1 ? PADDING.left + plotWidth / 2 : PADDING.left + (plotWidth * index) / (count - 1);
  const yFor = (value: number) => PADDING.top + plotHeight * (1 - (value - minY) / yRange);

  const hover = useChartHover(count);
  // Hit bands sit BETWEEN points, not on top of them — each band runs from the midpoint with its
  // previous neighbour to the midpoint with its next one, so the full plot width is hoverable
  // instead of only the few pixels a 2px-wide line actually covers.
  const xPositions = Array.from({ length: count }, (_, i) => xFor(i));
  const bandEdges = [
    PADDING.left,
    ...xPositions.slice(0, -1).map((x, i) => (x + xPositions[i + 1]!) / 2),
    plotRight,
  ];
  const labelIndices =
    count > LABEL_SKIP_THRESHOLD
      ? labels.map((_, i) => i).filter((i) => i % 2 === 0 || i === count - 1)
      : labels.map((_, i) => i);
  const activeIndex = hover.activeIndex;
  const activeDefinedYs =
    activeIndex != null
      ? series.map((s) => s.points[activeIndex]?.y).filter((y): y is number => y != null)
      : [];
  const activeMinY = activeDefinedYs.length > 0 ? Math.min(...activeDefinedYs.map(yFor)) : 0;

  // Same "current period" shading as `BarChart`'s `highlightFromIndex` — starts at the midpoint
  // between the previous point and this one (so it reads as starting *between* cycles), runs to
  // the plot's own right edge.
  const highlightX =
    highlightFromIndex != null && highlightFromIndex >= 0 && highlightFromIndex < count
      ? highlightFromIndex === 0
        ? PADDING.left
        : (xFor(highlightFromIndex - 1) + xFor(highlightFromIndex)) / 2
      : null;

  const legendNode = legend && (
    <div
      className={cn(
        'flex flex-wrap items-center gap-3 text-xs',
        legendPosition === 'top' ? 'mb-2' : 'mt-2',
      )}
    >
      {series.map((s) => (
        <div key={s.id} className="flex items-center gap-1.5 text-fg-subtle">
          {s.dashed ? (
            <span
              className="inline-block h-0 w-3 border-t-2 border-dashed"
              style={{ borderColor: s.color ?? 'var(--accent)' }}
            />
          ) : (
            <span
              className="inline-block size-2 rounded-full"
              style={{ backgroundColor: s.color ?? 'var(--accent)' }}
            />
          )}
          <span>{s.id}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div ref={drawInRef} className={cn('relative w-full', className)}>
      {legendPosition === 'top' && legendNode}
      <svg
        viewBox={`0 0 ${VIEW_WIDTH} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label={ariaLabel}
        preserveAspectRatio="none"
        {...hover.rootProps}
      >
        {showYAxis &&
          yAxisTicks.map((value, i) => (
            <g key={`y-axis-${i}`}>
              <line
                x1={PADDING.left}
                y1={yFor(value)}
                x2={plotRight}
                y2={yFor(value)}
                stroke="var(--border)"
                strokeOpacity={0.6}
                strokeWidth={1}
              />
              <text
                x={PADDING.left - 8}
                y={yFor(value) + 3}
                textAnchor="end"
                fontSize={10}
                fill="var(--fg-subtle)"
              >
                {formatAxisTick(value)}
              </text>
            </g>
          ))}

        {highlightX != null && (
          <rect
            x={highlightX}
            y={0}
            width={Math.max(0, plotRight - highlightX)}
            height={height}
            rx={8}
            fill="var(--accent-subtle)"
            style={{
              transition: `opacity ${DRAW_IN_DURATION} var(--ease)`,
              opacity: mounted ? 0.6 : 0,
            }}
          />
        )}

        <line
          x1={PADDING.left}
          y1={plotBottom}
          x2={plotRight}
          y2={plotBottom}
          stroke="var(--border)"
          strokeWidth={1}
        />

        {target && (
          <>
            <line
              x1={PADDING.left}
              y1={yFor(target.value)}
              x2={plotRight}
              y2={yFor(target.value)}
              stroke={target.color ?? 'var(--fg-subtle)'}
              strokeWidth={1}
              strokeDasharray="4 4"
            />
            {target.label && (
              <text
                x={plotRight}
                y={yFor(target.value) - 4}
                textAnchor="end"
                fontSize={10}
                fill="var(--fg-subtle)"
              >
                {target.label}
              </text>
            )}
          </>
        )}

        {series.map((s) => {
          const color = s.color ?? 'var(--accent)';
          const segments = buildSegments(s.points, xFor, yFor);
          const definedIndices = s.points
            .map((p, i) => (p.y != null ? i : null))
            .filter((i): i is number => i != null);
          const lastDefinedIndex =
            definedIndices.length > 0 ? definedIndices[definedIndices.length - 1] : undefined;
          return (
            <g key={s.id}>
              {s.area &&
                !s.dashed &&
                segments.map((segment, si) => {
                  if (segment.length < 2) return null;
                  const first = segment[0]!;
                  const last = segment[segment.length - 1]!;
                  const areaPoints = [
                    ...segment.map((p) => `${p.x},${p.y}`),
                    `${last.x},${plotBottom}`,
                    `${first.x},${plotBottom}`,
                  ].join(' ');
                  return (
                    <polygon
                      key={`${s.id}-area-${si}`}
                      points={areaPoints}
                      fill={color}
                      fillOpacity={0.12}
                      stroke="none"
                      style={{
                        transition: `opacity ${DRAW_IN_DURATION} var(--ease)`,
                        opacity: mounted ? 1 : 0,
                      }}
                    />
                  );
                })}
              {segments.map((segment, si) => (
                <polyline
                  key={`${s.id}-line-${si}`}
                  points={segment.map((p) => `${p.x},${p.y}`).join(' ')}
                  fill="none"
                  stroke={color}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  strokeDasharray={s.dashed ? '5 4' : 1}
                  pathLength={s.dashed ? undefined : 1}
                  style={
                    s.dashed
                      ? undefined
                      : {
                          strokeDashoffset: mounted ? 0 : 1,
                          transition: `stroke-dashoffset ${DRAW_IN_DURATION} var(--ease)`,
                        }
                  }
                />
              ))}
              {!s.dashed &&
                s.points.map((p, i) => {
                  if (p.y == null) return null;
                  // The most recent defined point on the caller's own series reads as "where
                  // things stand today" — a plain 3px dot the same size as every historical point
                  // buries that under seven identical marks. A bigger, filled marker (ringed in
                  // the surface colour so it still reads against the line under it) singles it out
                  // the same way the reference design's star badge does, without inventing a shape
                  // that needs its own legend entry.
                  const isCurrent = i === lastDefinedIndex && s.showValueLabels;
                  return (
                    <circle
                      key={`${s.id}-${p.x}`}
                      cx={xFor(i)}
                      cy={yFor(p.y)}
                      r={isCurrent ? 5.5 : 3}
                      fill={color}
                      stroke={isCurrent ? 'var(--surface)' : undefined}
                      strokeWidth={isCurrent ? 2 : undefined}
                      style={{
                        transition: `opacity 200ms var(--ease)`,
                        transitionDelay: DRAW_IN_DURATION,
                        opacity: mounted ? 1 : 0,
                      }}
                    />
                  );
                })}
              {s.showValueLabels &&
                !s.dashed &&
                s.points.map((p, i) => {
                  if (p.y == null) return null;
                  const cx = xFor(i);
                  const cy = yFor(p.y);
                  const isCurrent = i === lastDefinedIndex;
                  const text = formatPointLabel(p.y);
                  // Rough width estimate for the pill background — this is a short numeric label
                  // (rating values), never long enough for a real text-measurement pass to matter.
                  const pillWidth = Math.max(20, text.length * 6.5 + 10);
                  return (
                    <g
                      key={`${s.id}-label-${p.x}`}
                      style={{
                        transition: `opacity 200ms var(--ease)`,
                        transitionDelay: DRAW_IN_DURATION,
                        opacity: mounted ? 1 : 0,
                      }}
                    >
                      <rect
                        x={cx - pillWidth / 2}
                        y={cy - 26}
                        width={pillWidth}
                        height={16}
                        rx={8}
                        fill={isCurrent ? color : 'var(--surface)'}
                        stroke={isCurrent ? 'none' : 'var(--border)'}
                        strokeWidth={isCurrent ? 0 : 1}
                      />
                      <text
                        x={cx}
                        y={cy - 15}
                        textAnchor="middle"
                        fontSize={10}
                        fontWeight={isCurrent ? 700 : 500}
                        fill={isCurrent ? 'var(--fg-on-accent)' : 'var(--fg-muted)'}
                      >
                        {text}
                      </text>
                    </g>
                  );
                })}
            </g>
          );
        })}

        {labelIndices.map((i) => (
          <text
            key={labels[i]}
            x={xFor(i)}
            y={height - 6}
            // The first/last labels anchor at the plot's own edge (`xFor`'s first/last x IS
            // PADDING.left / VIEW_WIDTH - PADDING.right) — a centered anchor there runs half the
            // label off the edge of the viewBox. Only labels strictly between the ends can safely
            // center on their point; the two at the ends align inward instead.
            textAnchor={i === 0 ? 'start' : i === labels.length - 1 ? 'end' : 'middle'}
            fontSize={10}
            fill="var(--fg-subtle)"
          >
            {labels[i]}
          </text>
        ))}

        {/* A ring around the active point on every series (dashed included) — the hover/focus
            feedback a keyboard user gets in place of a mouse cursor sitting on the line. Skipped
            for a series with no value at this index (a suppressed/gap point has nothing to ring). */}
        {activeIndex != null &&
          series.map((s) => {
            const point = s.points[activeIndex];
            if (!point || point.y == null) return null;
            return (
              <circle
                key={`${s.id}-active`}
                cx={xFor(activeIndex)}
                cy={yFor(point.y)}
                r={5}
                fill="var(--surface)"
                stroke={s.color ?? 'var(--accent)'}
                strokeWidth={2}
              />
            );
          })}

        {/* Transparent per-index hit bands, one per label — sit above everything so hover/focus
            works over the line, the points and the empty space around them alike. */}
        {count > 0 &&
          bandEdges
            .slice(0, -1)
            .map((edge, i) => (
              <rect
                key={`band-${i}`}
                x={edge}
                y={PADDING.top}
                width={bandEdges[i + 1]! - edge}
                height={plotHeight}
                fill="transparent"
                onPointerEnter={() => hover.setActiveIndex(i)}
                onPointerMove={() => hover.setActiveIndex(i)}
              />
            ))}
      </svg>

      {activeIndex != null && activeDefinedYs.length > 0 && (
        <ChartTooltip
          x={(xPositions[activeIndex]! / VIEW_WIDTH) * 100}
          y={(activeMinY / height) * 100}
        >
          <div className="font-semibold text-fg">{labels[activeIndex]}</div>
          {series.map((s) => {
            const point = s.points[activeIndex];
            if (!point || point.y == null) return null;
            // Same precision as that series' own on-chart pill, so hovering never shows a
            // different-looking number than the label already sitting on the point — series with
            // no pill (the dashed benchmark line) keep the caller's own valueFormat.
            const displayValue =
              s.showValueLabels && !s.dashed ? formatPointLabel(point.y) : valueFormat(point.y);
            return (
              <div key={s.id} className="flex items-center gap-1.5">
                <span
                  className="inline-block size-2 rounded-full"
                  style={{ backgroundColor: s.color ?? 'var(--accent)' }}
                />
                <span className="text-fg-subtle">{s.id}</span>
                <span className="font-semibold text-fg">{displayValue}</span>
              </div>
            );
          })}
        </ChartTooltip>
      )}

      {legendPosition === 'bottom' && legendNode}
    </div>
  );
}
